import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CalendarPlus,
  Copy,
  MapPin,
  Navigation,
  Phone,
  RefreshCw,
  Ticket,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { EmptyState, Img, Shimmer } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { cancelBookingById, getBookingById, getMyBooking } from '@/api/apiUtils';
import KuickoRewardCard from '@/components/rewards/KuickoRewardCard';
import {
  STATUS_STYLES,
  canCancelBooking,
  buildBookingIcs,
  bookingStartsInFuture,
  downloadIcs,
  formatWhen,
  normalizeBooking,
  prettyStatus,
} from '@/lib/bookings';
import { formatINR } from '@/lib/catalog';

const Row = ({ label, value, strong, negative }) => (
  <div className="flex items-center justify-between py-2 text-sm">
    <span className={strong ? 'font-semibold text-ink-900' : 'text-ink-500'}>{label}</span>
    <span
      className={`${strong ? 'text-base font-bold text-ink-900' : 'font-medium text-ink-900'} ${
        negative ? 'text-green-600' : ''
      }`}
    >
      {negative ? '−' : ''}₹{formatINR(Math.abs(value))}
    </span>
  </div>
);

/** The "View details" screen: ticket, QR pass, venue and price breakdown. */
const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, openAuthModal } = useAuth();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [qr, setQr] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      let payload;
      try {
        const res = await getBookingById(id);
        payload = res?.data?.data || res?.data;
      } catch (err) {
        // GET /bookings/:id authorises by account *type*: for an organiser or
        // staff account it checks the booking belongs to *their venue*, not
        // that they bought it, so their own ticket purchases 404. The customer's
        // own list (/bookings/me) always includes them — read it from there.
        if (![403, 404].includes(err?.response?.status)) throw err;
        const res = await getMyBooking({});
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
        payload = list.find((b) => String(b?.id) === String(id));
        if (!payload) throw err;
      }
      setBooking(normalizeBooking(payload));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    load();
  }, [load]);

  // The QR encodes the booking code so venue staff can scan it at the gate.
  useEffect(() => {
    if (!booking?.id) return;
    QRCode.toDataURL(String(booking.code || booking.id), { margin: 1, width: 320 })
      .then(setQr)
      .catch(() => setQr(''));
  }, [booking]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(String(booking.code || booking.id));
      toast.success('Booking code copied');
    } catch {
      toast.error('Could not copy the code');
    }
  };

  // .ics rather than a Google-only link: iOS opens it straight into Calendar,
  // Android hands it to whichever calendar app is installed.
  const addToCalendar = () => {
    const ics = buildBookingIcs(booking, { url: window.location.href });
    if (!ics) {
      toast.error("This booking doesn't have a start time to add");
      return;
    }
    downloadIcs(ics, booking.title);
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      await cancelBookingById(booking.id, { reason: 'Cancelled by user' });
      toast.success('Booking cancelled');
      await load();
    } catch {
      toast.error('We could not cancel this booking');
    } finally {
      setCancelling(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <div className="lk-container flex-1 py-20">
          <EmptyState
            icon={Ticket}
            title="Sign in to view this booking"
            action={
              <button type="button" onClick={openAuthModal} className="lk-btn-primary">
                Log in
              </button>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-muted">
        <Header />
        <div className="lk-container grid gap-6 py-8 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Shimmer className="h-40 rounded-2xl" />
            <Shimmer className="h-64 rounded-2xl" />
          </div>
          <Shimmer className="h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error || !booking?.id) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <div className="lk-container flex-1 py-20">
          <EmptyState
            icon={AlertCircle}
            title="Booking not found"
            message="This booking may have been removed, or the link is out of date."
            action={
              <Link to="/my-bookings" className="lk-btn-primary">
                Back to my bookings
              </Link>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  const canCancel = canCancelBooking(booking);
  const isLive = !['cancelled', 'partially_cancelled', 'payment_failed', 'rejected'].includes(
    booking.status
  );
  const mapHref =
    booking.latitude && booking.longitude
      ? `https://www.google.com/maps/dir/?api=1&destination=${booking.latitude},${booking.longitude}`
      : null;

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />

      <main className="flex-1">
        <div className="lk-container py-6">
          <button
            type="button"
            onClick={() => navigate('/my-bookings')}
            className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-ink-500 transition-colors hover:text-ink-900"
          >
            <ArrowLeft className="h-4 w-4" />
            My bookings
          </button>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Header card */}
            <div className="lk-card animate-floatUp p-5 sm:p-6 lg:col-span-2">
                <div className="flex gap-4">
                  <Img src={booking.image} alt={booking.title} className="h-28 w-[86px] shrink-0 rounded-xl" />
                  <div className="min-w-0 flex-1">
                    <span
                      className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${
                        STATUS_STYLES[booking.status] || 'bg-surface-sunken text-ink-700 ring-ink-200'
                      }`}
                    >
                      {prettyStatus(booking.status)}
                    </span>
                    <h1 className="mt-2 text-h2 text-ink-900">
                      {booking.title}
                    </h1>
                    <p className="mt-1.5 flex items-center gap-1.5 text-sm text-ink-700">
                      <Calendar className="h-4 w-4 text-ink-400" />
                      {formatWhen(booking.date, booking.time)}
                    </p>
                    {booking.category && (
                      <p className="mt-0.5 text-sm text-ink-500">{booking.category}</p>
                    )}
                  </div>
                </div>
              </div>

            {/* Pass. DOM order puts it right after the header card, so on a
                phone the QR is on the first screen instead of below tickets,
                venue and payment. On desktop it spans both rows in column 3
                and stays pinned, as before. */}
            <aside className="lg:col-start-3 lg:row-span-2 lg:row-start-1">
              <div className="space-y-4 lg:sticky lg:top-24">
                <div className="lk-card overflow-hidden">
                  <div className="border-b border-dashed border-ink-200 p-6 text-center">
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-ink-400">
                      Entry pass
                    </p>
                    <div className="mx-auto mt-4 w-44">
                      {qr && isLive ? (
                        <img src={qr} alt="Booking QR code" className="w-full rounded-xl" />
                      ) : (
                        <div className="flex aspect-square items-center justify-center rounded-xl bg-surface-sunken text-sm text-ink-400">
                          {isLive ? 'Generating…' : 'Cancelled'}
                        </div>
                      )}
                    </div>
                    <p className="mt-4 text-2xl font-bold tracking-wider text-ink-900">
                      {booking.code || `#${booking.id}`}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={copyCode}
                        className="lk-focus inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-4 text-sm font-semibold text-brand-secondary hover:bg-brand-tint active:scale-95"
                      >
                        <Copy className="h-4 w-4" aria-hidden="true" />
                        Copy code
                      </button>
                      {isLive && bookingStartsInFuture(booking) && (
                        <button
                          type="button"
                          onClick={addToCalendar}
                          className="lk-focus inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-4 text-sm font-semibold text-brand-secondary hover:bg-brand-tint active:scale-95"
                        >
                          <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                          Add to calendar
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1 p-5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-ink-500">Booking ID</span>
                      <span className="font-medium text-ink-900">#{booking.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Tickets</span>
                      <span className="font-medium text-ink-900">{booking.ticketCount || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Amount</span>
                      <span className="font-medium text-ink-900">₹{formatINR(booking.amount)}</span>
                    </div>
                  </div>
                </div>

                {isLive && (
                  <p className="rounded-xl bg-white/70 px-4 py-3 text-xs leading-relaxed text-ink-500">
                    Show this QR code along with a valid photo ID at the venue entrance.
                  </p>
                )}

                {/* Two steps: this used to cancel a paid booking on the first
                    tap, with no confirmation — one mis-tap on a phone was enough.
                    The list page already asks; this now does too. */}
                {canCancel && !confirmingCancel && (
                  <button
                    type="button"
                    onClick={() => setConfirmingCancel(true)}
                    className="lk-btn w-full border border-red-200 bg-white text-red-600 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                    Cancel booking
                  </button>
                )}
                {canCancel && confirmingCancel && (
                  <div role="alertdialog" aria-label="Confirm cancellation" className="rounded-2xl border border-red-200 bg-red-50 p-4">
                    <p className="text-sm font-semibold text-red-800">Cancel this booking?</p>
                    <p className="mt-1 text-xs leading-relaxed text-red-700">
                      Any refund follows the organiser's cancellation policy. This can't be undone.
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmingCancel(false)}
                        disabled={cancelling}
                        className="lk-btn-ghost flex-1"
                      >
                        Keep it
                      </button>
                      <button
                        type="button"
                        onClick={cancel}
                        disabled={cancelling}
                        className="lk-btn flex-1 bg-red-600 text-white hover:bg-red-700"
                      >
                        {cancelling ? <RefreshCw className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                        Yes, cancel
                      </button>
                    </div>
                  </div>
                )}

                {booking.cancelReason && (
                  <p className="rounded-xl bg-red-50 px-4 py-3 text-xs text-red-700">
                    Cancellation reason: {booking.cancelReason}
                  </p>
                )}
              </div>
            </aside>

            <div className="space-y-6 lg:col-span-2">
              {/* Kuicko reward earned by this booking — renders nothing if none. */}
              <KuickoRewardCard bookingId={booking.id} className="" />

              {/* Ticket lines */}
              {booking.lines.length > 0 && (
                <div className="lk-card p-5 sm:p-6">
                  <h2 className="mb-4 text-lg font-bold text-ink-900">Tickets</h2>
                  <ul className="divide-y divide-ink-200/70">
                    {booking.lines.map((line, i) => (
                      <li key={line.id ?? i} className="flex items-start justify-between gap-4 py-3">
                        <div className="min-w-0">
                          <p className="font-medium text-ink-900">{line.name}</p>
                          <p className="text-sm text-ink-500">
                            {line.quantity} × ₹{formatINR(line.price)}
                          </p>
                          {line.seatLabels?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {line.seatLabels.map((label) => (
                                <span
                                  key={label}
                                  className="rounded bg-brand-tint px-2 py-0.5 text-[11px] font-semibold text-brand-secondary"
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="font-semibold text-ink-900">
                          ₹{formatINR(line.price * line.quantity)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Venue */}
              {(booking.venue || booking.providerName) && (
                <div className="lk-card p-5 sm:p-6">
                  <h2 className="mb-4 text-lg font-bold text-ink-900">Venue</h2>
                  {booking.providerName && (
                    <p className="font-semibold text-ink-900">{booking.providerName}</p>
                  )}
                  <p className="mt-1 flex items-start gap-2 text-sm text-ink-700">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ink-400" />
                    {[booking.venue, booking.city].filter(Boolean).join(', ') || 'Address not provided'}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {mapHref && (
                      <a href={mapHref} target="_blank" rel="noreferrer" className="lk-btn-ghost">
                        <Navigation className="h-4 w-4" />
                        Directions
                      </a>
                    )}
                    {booking.providerPhone && (
                      <a href={`tel:${booking.providerPhone}`} className="lk-btn-ghost">
                        <Phone className="h-4 w-4" />
                        Call venue
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Price breakdown */}
              <div className="lk-card p-5 sm:p-6">
                <h2 className="mb-2 text-lg font-bold text-ink-900">Payment summary</h2>
                <div className="divide-y divide-ink-200/70">
                  {booking.subtotal > 0 && <Row label="Subtotal" value={booking.subtotal} />}
                  {booking.discount > 0 && <Row label="Discount" value={booking.discount} negative />}
                  {booking.platformFee > 0 && <Row label="Platform fee" value={booking.platformFee} />}
                  {booking.tax > 0 && <Row label="Taxes" value={booking.tax} />}
                  <Row label="Total paid" value={booking.amount} strong />
                </div>
                {booking.paymentStatus && (
                  <p className="mt-3 text-xs text-ink-500">
                    Payment status: {prettyStatus(booking.paymentStatus)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookingDetails;
